<template>
  <div class="h-full overflow-y-auto">
    <!-- 表格头部 -->
    <div class="sticky top-0 bg-white dark:bg-gray-800 z-10 border-b border-gray-200 dark:border-gray-700">
      <div class="flex min-w-full">
        <div class="w-[8%] py-3.5 px-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">ID</div>
        <div class="w-[15%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">套餐分类</div>
        <div class="w-[12%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">套餐代码</div>
        <div class="w-[20%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">套餐名称</div>
        <div class="w-[10%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">排序</div>
        <div class="w-[12%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">状态</div>
        <div class="w-[23%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">操作</div>
      </div>
    </div>

    <!-- 表格内容 -->
    <div class="divide-y divide-gray-200 dark:divide-gray-700">
      <div v-for="plan in plans" :key="plan.id" class="flex items-center min-w-full hover:bg-indigo-50 dark:hover:bg-indigo-900/30 cursor-pointer"
        :class="{ 'bg-gray-100 dark:bg-gray-700 opacity-60': plan.isHidden }" @click="handleRowClick(plan)">
        <div class="w-[8%] py-4 px-3 text-sm text-gray-900 dark:text-gray-100">{{ plan.id }}</div>
        <div class="w-[15%] px-3 py-4 text-sm text-gray-600 dark:text-gray-400">
          {{ getCategoryName(plan.categoryId) }}
        </div>
        <div class="w-[12%] px-3 py-4 text-sm">
          <code class="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded text-xs font-mono">
            {{ plan.planCode }}
          </code>
        </div>
        <div class="w-[20%] px-3 py-4 text-sm text-gray-900 dark:text-gray-100 font-medium">{{ displayLocalizedText(plan.name) }}</div>
        <div class="w-[10%] px-3 py-4 text-sm text-gray-600 dark:text-gray-400">{{ plan.displayOrder }}</div>
        <div class="w-[12%] px-3 py-4 text-sm">
          <div class="flex flex-wrap gap-1">
            <!-- 启用/禁用标签 -->
            <span v-if="!plan.isActive"
              class="inline-flex items-center rounded-full bg-gray-50 dark:bg-gray-700 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400">
              禁用
            </span>
            <span v-else
              class="inline-flex items-center rounded-full bg-green-50 dark:bg-green-900/30 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-400">
              启用
            </span>

            <!-- 隐藏标签（可点击切换） -->
            <span v-if="plan.isHidden" @click.stop="handleToggleHidden(plan)"
              class="inline-flex items-center rounded-full bg-gray-200 dark:bg-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors">
              已隐藏
            </span>
            <span v-else @click.stop="handleToggleHidden(plan)"
              class="inline-flex items-center rounded-full bg-green-50 dark:bg-green-900/30 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-400 cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors">
              ✓ 显示中
            </span>
          </div>
        </div>
        <div class="w-[23%] px-3 py-4 text-sm flex gap-2" @click.stop>
          <button class="link-btn-edit font-medium" @click="handleEdit(plan)" title="编辑">
            编辑
          </button>
          <button class="text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-300 font-medium" @click="handleViewPricings(plan)"
            title="查看价格方案">
            价格
          </button>
          <button class="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 font-medium" @click="handleViewFeatures(plan)"
            title="查看功能">
            功能
          </button>
          <button class="text-orange-600 dark:text-orange-400 hover:text-orange-900 dark:hover:text-orange-300 font-medium" @click="handleSyncQuotas(plan)"
            title="同步配额到现有用户">
            同步
          </button>
          <button class="link-btn-delete font-medium" @click="handleDelete(plan)" title="删除">
            删除
          </button>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-if="plans.length === 0" class="text-center py-12 text-gray-500 dark:text-gray-400">
      暂无套餐数据
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Plan } from '@/types/membership/plan'
import type { PlanCategory } from '@/types/membership/category'
import { displayLocalizedText } from '@/types/membership/localized'

/**
 * Props
 */
interface Props {
  /** 套餐列表 */
  plans: Plan[]
  /** 套餐分类列表（用于显示分类名称） */
  categories: PlanCategory[]
}

const props = defineProps<Props>()

/**
 * Emits
 */
const emit = defineEmits<{
  /** 编辑套餐 */
  edit: [plan: Plan]
  /** 删除套餐 */
  delete: [plan: Plan]
  /** 查看价格方案 */
  viewPricings: [plan: Plan]
  /** 查看功能 */
  viewFeatures: [plan: Plan]
  /** 同步配额 */
  syncQuotas: [plan: Plan]
  /** 行点击 */
  rowClick: [plan: Plan]
  /** 切换隐藏状态 */
  toggleHidden: [plan: Plan]
}>()

/**
 * 获取分类名称
 */
const getCategoryName = (categoryId: number): string => {
  const category = props.categories.find(c => c.id === categoryId)
  return category ? displayLocalizedText(category.name) : `分类 ${categoryId}`
}

/**
 * 处理编辑
 */
const handleEdit = (plan: Plan) => {
  emit('edit', plan)
}

/**
 * 处理删除
 */
const handleDelete = (plan: Plan) => {
  emit('delete', plan)
}

/**
 * 处理查看价格方案
 */
const handleViewPricings = (plan: Plan) => {
  emit('viewPricings', plan)
}

/**
 * 处理查看功能
 */
const handleViewFeatures = (plan: Plan) => {
  emit('viewFeatures', plan)
}

/**
 * 处理同步配额
 */
const handleSyncQuotas = (plan: Plan) => {
  emit('syncQuotas', plan)
}

/**
 * 处理行点击
 */
const handleRowClick = (plan: Plan) => {
  emit('rowClick', plan)
}

/**
 * 处理切换隐藏状态
 */
const handleToggleHidden = (plan: Plan) => {
  emit('toggleHidden', plan)
}
</script>
